import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { LoadingButton } from '@mui/lab';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// hooks
// components
import Logo from 'src/components/logo';
import Scrollbar from 'src/components/scrollbar';
import { usePathname, useRouter } from 'src/routes/hooks';
import { NavSectionVertical } from 'src/components/nav-section';
//
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import {
  Avatar,
  Divider,
  ListItemText,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { getCreditDetails, getOrganizationId } from 'src/services/user.service';
import SvgColor from 'src/components/svg-color';
import { paths } from 'src/routes/paths';
import PricingPlansDialog from 'src/sections/pricing-plans/pricing-plans-dialog';
import { useNavData } from './config-navigation';
import { NAV } from '../config-layout';
import { useQuery } from 'src/hooks/use-query';

// ----------------------------------------------------------------------

export default function NavVertical({ openNav, onCloseNav }) {
  // hooks
  const { user, logout, organization } = useAuthContext();
  const pathname = usePathname();
  const lgUp = useResponsive('up', 'lg');
  const navData = useNavData();
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const popover = usePopover();
  const queryParams = useQuery();
  const showPricingPlan = queryParams.get('pricing');

  // variables
  const organization_id = getOrganizationId(user);

  // states
  const [availableCredit, setAvailableCredit] = useState(0);
  const [isCreditLoading, setIsCreditLoading] = useState(true);
  const [openPricingPlans, setOpenPricingPlans] = useState(false);

  const OPTIONS = [
    {
      label: 'Teams',
      linkTo: paths.dashboard.settings.teams,
    },
    {
      label: 'Organization',
      linkTo: paths.dashboard.settings.organization,
    },
    {
      label: 'Settings',
      linkTo: paths.dashboard.settings.notification,
    },
    // {
    //   label: 'Manage Account',
    //   linkTo: '/dashboard/manage-account',
    // },
    {
      label: 'Change Password',
      linkTo: '/dashboard/change-password',
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      router.replace('/');
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClickItem = path => {
    popover.onClose();
    router.push(path);
  };

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (showPricingPlan && showPricingPlan === 'true') {
      setOpenPricingPlans(true);
    }
  }, [showPricingPlan]);

  useEffect(() => {
    const getCreditDetailsCaller = async () => {
      setIsCreditLoading(true);
      try {
        const res = await getCreditDetails(organization_id);
        if (res && res.body) {
          setAvailableCredit(res.body.available_credit);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsCreditLoading(false);
      }
    };
    getCreditDetailsCaller();
  }, [organization_id]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: '29.03px',
          pl: '12px',
        },
      }}
    >
      <Logo
        sx={{
          mt: '25px',
          mb: '12.03px',
          pl: '23px',
        }}
      />

      <Button
        onClick={() =>
          pathname === '/dashboard/jobs'
            ? null
            : router.push(paths.dashboard.jobs.root)
        }
        sx={{
          border: theme => `1px solid ${theme.palette.border.primaryBlack3}`,
          background: 'transparent',
          borderRadius: '4px',
          padding: '8px',
          display: 'flex',
          justifyContent: 'start',
          minWidth: 'auto',
          width: '226px',
          mt: '12px',
          mx: '12px',
          gap: '12px',
          visibility: pathname === '/dashboard/jobs' ? 'hidden' : 'visible',
        }}
        startIcon={<Iconify icon="tabler:arrow-left" />}
      >
        <Typography variant="input" fontWeight={500}>
          Back
        </Typography>
      </Button>

      <NavSectionVertical
        data={navData}
        config={{
          currentRole: user?.user_role || 'ADMIN',
        }}
      />

      <Box sx={{ flexGrow: 1 }} />

      <Stack alignItems="center" px="12px" spacing="12px">
        {user && user.user_role === 'ADMIN' && (
          <Button
            disabled={isCreditLoading}
            fullWidth
            variant="contained"
            onClick={() => {
              if (availableCredit < 1) {
                setOpenPricingPlans(true);
              } else {
                router.push(paths.dashboard.jobs.new);
              }
            }}
            startIcon={<Iconify icon="tabler:plus" />}
          >
            Create Job
          </Button>
        )}
        {organization &&
          organization.payment_profile &&
          organization.payment_profile !== 'ENTERPRISE' && (
            <MenuItem
              onClick={() => setOpenPricingPlans(true)}
              sx={{
                pl: '10px',
                py: '16px',
                width: '100%',
                bgcolor: theme => theme.palette.background.primaryGrey1,
              }}
            >
              <Stack direction="row" alignItems="center">
                <Box
                  sx={{
                    p: '2.33px',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: '8px',
                    backgroundColor: theme =>
                      theme.palette.background.primaryAccent2,
                  }}
                >
                  <SvgColor
                    sx={{ color: theme => theme.palette.content.accent1 }}
                    src="/assets/icons/job/ic_star_twinkle.svg"
                  />
                </Box>
                <Typography
                  fontWeight={600}
                  color={theme => theme.palette.content.accent1}
                  mr="4px"
                >
                  Upgrade Plan
                </Typography>
              </Stack>
            </MenuItem>
          )}
        <MenuItem
          sx={{
            width: '100%',
            px: '8px',
            py: '10px',
          }}
          onClick={popover.onOpen}
        >
          <Stack direction="row" alignItems="center" width="100%">
            <Avatar
              sx={{
                width: 40,
                height: 40,
              }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>

            <ListItemText
              primary={user?.name}
              primaryTypographyProps={{ typography: 'input', fontWeight: 600 }}
              secondaryTypographyProps={{
                typography: 'caption',
                color: 'content.primaryBlack3',
              }}
              sx={{
                ml: '8px',
                mr: '45px',
              }}
            />
            <Box flexGrow={1} />
            <Iconify
              width={20}
              icon="tabler:chevron-up"
              sx={{ color: theme => theme.palette.content.primaryBlack3 }}
            />
          </Stack>
        </MenuItem>
      </Stack>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ marginTop: '-50px', width: '220px' }}
      >
        <Stack spacing="8px">
          <Stack px="7px" spacing="4px">
            <Typography variant="paragraph" fontWeight={600} noWrap>
              {user?.name}
            </Typography>

            <Typography variant="input" lineHeight="normal" noWrap>
              {user?.email}
            </Typography>
          </Stack>

          <Divider sx={{ borderStyle: 'dashed' }} />

          <Stack>
            {OPTIONS.map(option => {
              if (
                option.label === 'Teams' &&
                user &&
                (user.user_role === 'MEMBER' || user.user_role === null)
              )
                return null;
              return (
                <MenuItem
                  key={option.label}
                  onClick={() => {
                    handleClickItem(option.linkTo);
                  }}
                >
                  {option.label}
                </MenuItem>
              );
            })}
          </Stack>

          <Divider sx={{ borderStyle: 'dashed' }} />

          <MenuItem
            onClick={handleLogout}
            sx={{ fontWeight: 'fontWeightBold', color: 'error.main' }}
          >
            Logout
          </MenuItem>
        </Stack>
      </CustomPopover>
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: 0,
        width: NAV.W_VERTICAL,
      }}
    >
      {/* <NavToggleButton /> */}
      <Stack
        sx={{
          height: 1,
          position: 'fixed',
          width: NAV.W_VERTICAL,
          bgcolor: theme => theme.palette.background.primaryWhite1,
        }}
      >
        {renderContent}
      </Stack>
      {/* {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            bgcolor: theme => theme.palette.background.primaryWhite1,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )} */}
      <PricingPlansDialog
        open={openPricingPlans}
        onClose={() => {
          if (showPricingPlan && showPricingPlan === 'true') {
            router.replace(`${paths.dashboard.jobs.root}?status=ACTIVE`);
          }
        }}
        setOpenPricingPlans={setOpenPricingPlans}
      />
    </Box>
  );
}

NavVertical.propTypes = {
  onCloseNav: PropTypes.func,
  openNav: PropTypes.bool,
};
